import { Check, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Icon } from "../../icons/Icon";
import { cx } from "../../styles";

/**
 * A sequence, vertically or horizontally.
 *
 * The same items either way: `Timeline` is a record's history — what happened,
 * when, and by whom — and `Stepper` is a process somebody is part-way through.
 * One data shape, because the difference is layout rather than meaning, and a
 * screen that shows the same seventeen order statuses as a history on one page
 * and a progress bar on another should not describe them twice.
 *
 * Both render an ordered list. The order is the content, and `<ol>` is what
 * says so to a screen reader — "list, 4 items, item 2 of 4" is most of what a
 * stepper communicates visually.
 */

export type StepState = "done" | "current" | "pending";

export interface TimelineItem {
	id: string;
	title: string;
	/** When, or who. Rendered under the title in the muted weight. */
	meta?: ReactNode;
	icon?: LucideIcon;
	state: StepState;
}

const DOT: Record<StepState, string> = {
	done: "border-brand bg-brand text-fg-on-brand",
	// Hollow with a thick brand ring: the one somebody is on reads as open
	// rather than finished, which a filled dot would not.
	current: "border-[3px] border-brand bg-bg-surface text-brand-ink",
	pending: "border-border-strong bg-bg-surface text-fg-subtle",
};

const LABEL: Record<StepState, string> = {
	done: "text-fg",
	current: "font-bold text-fg",
	pending: "text-fg-muted",
};

export interface TimelineProps {
	items: TimelineItem[];
	className?: string;
}

/** A record's history, newest last. */
export function Timeline({ items, className }: TimelineProps) {
	return (
		<ol className={cx("flex flex-col", className)}>
			{items.map((item, index) => (
				<li key={item.id} className="flex gap-3">
					<div className="flex flex-col items-center">
						<Dot item={item} />
						{/* The rail joins this dot to the next one, so the last item has
						    none — a trailing stub reads as a step nobody drew. */}
						{index < items.length - 1 && (
							<span
								className={cx("w-0.5 flex-1", item.state === "done" ? "bg-brand" : "bg-border")}
							/>
						)}
					</div>
					<div className={cx("flex flex-col gap-0.5 pb-4", LABEL[item.state])}>
						<p className="text-[13px]">{item.title}</p>
						{item.meta !== undefined && <p className="text-[11.5px] text-fg-subtle">{item.meta}</p>}
					</div>
				</li>
			))}
		</ol>
	);
}

export interface StepperProps {
	items: TimelineItem[];
	/** Names the process for assistive technology. */
	"aria-label"?: string;
	className?: string;
}

/**
 * A process, left to right — and top to bottom on a phone.
 *
 * The vertical fallback under 640px is not a nicety: five steps across a 375px
 * screen gives each one seventy pixels, which fits neither a label nor a
 * readable dot. Stacking is the only honest way to show the same thing.
 */
export function Stepper({ items, className, ...labelling }: StepperProps) {
	return (
		<ol
			className={cx(
				"flex gap-0",
				// Vertical below the breakpoint, horizontal above it. A flex
				// direction rather than a second component, so the items are written
				// once.
				"max-sm:flex-col",
				className,
			)}
			{...labelling}
		>
			{items.map((item, index) => (
				<li
					key={item.id}
					className="flex min-w-0 flex-1 gap-3 max-sm:gap-3 sm:flex-col sm:items-center sm:gap-2"
				>
					<div className="flex flex-col items-center sm:w-full sm:flex-row">
						{/* The connector before the dot, hidden on the first step: a rail
						    running off the start edge suggests a step that came earlier. */}
						<span
							className={cx(
								"max-sm:hidden sm:h-0.5 sm:flex-1",
								index === 0 && "sm:invisible",
								item.state === "pending" ? "sm:bg-border" : "sm:bg-brand",
							)}
						/>
						<Dot item={item} />
						<span
							className={cx(
								"sm:hidden",
								index < items.length - 1 ? "w-0.5 flex-1" : "hidden",
								item.state === "done" ? "bg-brand" : "bg-border",
							)}
						/>
						<span
							className={cx(
								"max-sm:hidden sm:h-0.5 sm:flex-1",
								index === items.length - 1 && "sm:invisible",
								item.state === "done" ? "sm:bg-brand" : "sm:bg-border",
							)}
						/>
					</div>

					<div
						className={cx(
							"flex flex-col gap-0.5 pb-4 sm:items-center sm:pb-0 sm:text-center",
							LABEL[item.state],
						)}
					>
						<p className="text-[12.5px]">{item.title}</p>
						{item.meta !== undefined && <p className="text-[11px] text-fg-subtle">{item.meta}</p>}
					</div>
				</li>
			))}
		</ol>
	);
}

function Dot({ item }: { item: TimelineItem }) {
	return (
		<span
			// The state is on the element rather than only in its colour, so a
			// finished step is distinguishable from a pending one by something
			// other than hue — and so a test can see it.
			data-state={item.state}
			aria-current={item.state === "current" ? "step" : undefined}
			className={cx(
				"grid size-[26px] shrink-0 place-items-center rounded-full border-2",
				DOT[item.state],
			)}
		>
			{item.state === "done" && item.icon === undefined ? (
				<Icon as={Check} size={14} />
			) : item.icon !== undefined ? (
				<Icon as={item.icon} size={13} />
			) : null}
		</span>
	);
}
